
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { PostEditorState } from "@/types/postEditorTypes";

export const createInitialState = (): PostEditorState => ({
  // Basic fields
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  
  // Enhanced fields
  author: "",
  seoDescription: "",
  theme: "default",
  categories: [],
  isDraft: true,
  
  // UI state
  featuredImage: "",
  featuredImageAlt: "",
  featuredImageTitle: "",
  isPending: false,
});

export const usePostEditorState = (postId?: string) => {
  const [state, setState] = useState<PostEditorState>(createInitialState());
  const [isLoading, setIsLoading] = useState(postId ? true : false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();

  // Update state functions
  const updateState = (field: keyof PostEditorState, value: any) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  const setTitle = (title: string) => updateState('title', title);
  const setSlug = (slug: string) => updateState('slug', slug);
  const setExcerpt = (excerpt: string) => updateState('excerpt', excerpt);
  const setContent = (content: string) => updateState('content', content);
  const setAuthor = (author: string) => updateState('author', author);
  const setSeoDescription = (desc: string) => updateState('seoDescription', desc);
  const setTheme = (theme: string) => updateState('theme', theme);
  const setCategories = (categories: string[]) => updateState('categories', categories);
  const setIsDraft = (isDraft: boolean) => updateState('isDraft', isDraft);
  const setFeaturedImage = (url: string) => updateState('featuredImage', url);
  const setFeaturedImageAlt = (alt: string) => updateState('featuredImageAlt', alt);
  const setFeaturedImageTitle = (title: string) => updateState('featuredImageTitle', title);
  const setIsPending = (isPending: boolean) => updateState('isPending', isPending);

  const togglePublishStatus = () => {
    setState(prev => ({ ...prev, isDraft: !prev.isDraft }));
  };

  return {
    state,
    isLoading,
    showDeleteDialog,
    setShowDeleteDialog,
    setTitle,
    setSlug,
    setExcerpt,
    setContent,
    setAuthor,
    setSeoDescription,
    setTheme,
    setCategories,
    setFeaturedImage,
    setFeaturedImageAlt,
    setFeaturedImageTitle,
    setIsLoading,
    setIsPending,
    setIsDraft,
    togglePublishStatus,
    toast
  };
};
